import React from 'react';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeError } from '@stripe/stripe-js';

import type { StripeFailure } from '@/utils/session-checkout-accounting';
import { stripeLocale } from '@/utils/stripe-locale';
import { getStripe, useStripeAppearance } from '../../../../data/payments';

/** What one press of Pay came to. Returned to the checkout hook, which owns every decision that
 *  follows: the busy state, the error banner, the session accounting, the confirmation. */
export interface CardPayOutcome {
  /** Stripe accepted the payment (cleared, or settling asynchronously). */
  ok: boolean;
  /**
   * Stripe validated and collected the card details before whatever happened next. Distinguishes
   * a buyer whose card was declined from one who pressed Pay on a form they never filled in —
   * two very different funnel stories that a bare failure count cannot tell apart.
   */
  paymentInfoEntered: boolean;
  /** Stripe's own reason, when it failed. */
  error?: StripeFailure;
  /** How Stripe left the intent, when it succeeded. */
  status?: 'processing' | 'succeeded';
}

/** A function that runs one payment attempt, handed up by {@link CardFields} once Stripe is
 *  mounted. `null` deregisters it (the fields went away). */
export type CardPayFn = (() => Promise<CardPayOutcome>) | null;

export type CardFieldsProps = {
  /** PaymentIntent client secret for this session's invoice. */
  clientSecret: string;
  /** Pre-fills the card form's billing email with the one collected above it. */
  email: string;
  /** Publish (or withdraw) the attempt function, so the shared footer button can run it. */
  onRegisterPay: (pay: CardPayFn) => void;
};

/** Stripe's error object, mapped onto the vocabulary the session accounting persists. Kept
 *  verbatim — support looks these codes up, so paraphrasing them would lose the diagnosis. */
function toFailure(error: StripeError): StripeFailure {
  return {
    code: error.code,
    declineCode: error.decline_code,
    type: error.type,
    message: error.message,
  };
}

/**
 * The card form itself, plus the attempt it knows how to run.
 *
 * `useStripe`/`useElements` only work under an `<Elements>` provider, but the Pay button belongs
 * to the checkout's shared footer, OUTSIDE it — one button for both rails is the whole point of
 * the accordion. So rather than hoisting the provider around the entire form (which remounts it,
 * and would blank the email field the moment the card became ready), this publishes its attempt
 * function upwards and the footer calls it.
 *
 * It deliberately DECIDES nothing: it performs the two Stripe calls and reports what happened.
 * The busy state, the error copy, the session accounting and the confirmation all stay with the
 * checkout hook, so the two rails behave identically on everything except the payment itself.
 */
const CardForm = ({ clientSecret, email, onRegisterPay }: CardFieldsProps): React.ReactElement => {
  const stripe = useStripe();
  const elements = useElements();

  const pay = React.useCallback(async (): Promise<CardPayOutcome> => {
    if (!stripe || !elements) return { ok: false, paymentInfoEntered: false };

    // Validate + collect the card first, then confirm — the order Stripe requires, and the
    // point at which we know the buyer really did enter payment details.
    const submitResult = await elements.submit();
    if (submitResult.error) {
      return { ok: false, paymentInfoEntered: false, error: toFailure(submitResult.error) };
    }

    const result = await stripe.confirmPayment({
      elements,
      clientSecret,
      redirect: 'if_required',
      confirmParams: { return_url: window.location.origin + window.location.pathname },
    });
    if (result.error) {
      return { ok: false, paymentInfoEntered: true, error: toFailure(result.error) };
    }

    // A declined card comes back as `result.error` above, so reaching here means Stripe accepted
    // the payment: `succeeded` for a card, or `processing` for a method that settles
    // asynchronously. Both are "the buyer has paid", which is what the acknowledgement reports; a
    // charge that later fails moves the session to `payment_failed` and the retry banner picks it
    // up. A redirect-based method never reaches this line at all (the page navigates away and
    // comes back to `return_url`, where the entry probe finds the paid invoice instead).
    return {
      ok: true,
      paymentInfoEntered: true,
      status: result.paymentIntent?.status === 'processing' ? 'processing' : 'succeeded',
    };
  }, [stripe, elements, clientSecret]);

  // Publish while mounted, withdraw on the way out — so the footer can never run an attempt
  // against a form that is no longer on screen (the buyer switched back to the bank transfer).
  React.useEffect(() => {
    onRegisterPay(pay);
    return () => onRegisterPay(null);
  }, [pay, onRegisterPay]);

  return <PaymentElement options={{ defaultValues: { billingDetails: { email } } }} />;
};

/**
 * The card rail's fields: Stripe Elements, themed to the studio.
 *
 * Mounted only once the invoice has yielded a client secret — Elements cannot render a card form
 * without one — which is why selecting the card is what commits the session to that rail.
 */
export const CardFields = (props: CardFieldsProps): React.ReactElement => {
  // Re-themes live on a light ⇄ dark switch: Stripe renders Elements in a cross-origin iframe
  // that cannot read our CSS custom properties, so the palette is resolved in React and passed
  // as concrete literals.
  const appearance = useStripeAppearance();
  // Stripe renders its OWN copy (field labels, validation errors) and defaults to `auto`, which
  // reads the BROWSER's language — so a buyer with an English browser got an English card form
  // inside a Spanish checkout. Take the studio's active language instead, mapped to a locale
  // Stripe actually has (`mx` is our own key, and Stripe's Mexican Spanish is `es-419`).
  const { i18n } = useTranslation();
  const locale = stripeLocale(i18n.language);
  return (
    <Elements
      // `locale` and `clientSecret` are NOT mutable after mount — unlike `appearance`, which
      // Stripe applies in place. Keying on the locale remounts Elements on a language switch
      // instead of leaving it stuck in the language the checkout opened in.
      key={locale}
      stripe={getStripe()}
      options={{ clientSecret: props.clientSecret, appearance, locale }}
    >
      <CardForm {...props} />
    </Elements>
  );
};

export default CardFields;
